import {isFabIconPresetUrl} from '@/lib/fab-icon-presets'
import {
  type PanelPlacement,
  type PanelPresenter,
  CenterModalPanel,
  SideSheetPanel,
  presenterKindForPlacement,
} from './panel-presenter'
import {applyPanelCornerRadius} from './sheet-shell-shared'
import {Shell, type ShellOptions} from './shell-base'
import {ShellStyles} from './shell-styles'
import {SideSheetMobileDrawerStyles} from './side-sheet-layout'
import type {AnagramMessage, EnvironmentConfig, FabConfig, ShellConfig} from './types'

declare const __BITS_CSS__: string
const BitsStyles: string = typeof __BITS_CSS__ !== 'undefined' ? __BITS_CSS__ : ''

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

export class FloatingButtonShell extends Shell {
  private shellConfig: FabConfig
  private host: HTMLDivElement | null = null
  private root: HTMLDivElement | null = null
  private trigger!: HTMLButtonElement
  private presenter!: PanelPresenter
  private currentIframe: HTMLIFrameElement | null = null
  private currentIframeContextKey: string | null = null
  private isOpen = false
  private isIframeReady = false
  private viewportResizeListener: (() => void) | null = null

  constructor(config: EnvironmentConfig, experienceId: string, shellConfig: FabConfig, options?: ShellOptions) {
    super(config, experienceId, options)
    this.shellConfig = shellConfig
  }

  mount(): void {
    const fontFamily = this.getShellFontStack(this.shellConfig.fontFamily)

    this.host = document.createElement('div')
    this.host.id = 'anagram-shell'
    this.styleAsViewportOverlay(this.host)
    document.body.appendChild(this.host)

    const shadow = this.host.attachShadow({mode: 'open'})
    this.attachFontStylesheet(shadow, this.shellConfig.fontFamily)

    const style = document.createElement('style')
    style.textContent = ShellStyles + BitsStyles + (this.usesSideSheetLayout ? SideSheetMobileDrawerStyles : '')
    shadow.appendChild(style)

    const root = document.createElement('div')
    root.className = 'anagram-root'
    root.style.fontFamily = fontFamily
    applyPanelCornerRadius(root, this.shellConfig.theme?.['--radius'])
    shadow.appendChild(root)
    this.root = root

    this.trigger = document.createElement('button')
    this.trigger.className = 'anagram-trigger'
    this.trigger.style.fontFamily = fontFamily
    this.trigger.setAttribute('aria-label', 'Open Anagram')
    this.renderTriggerChrome()
    this.applyTriggerAlignment(this.shellConfig.panelLocation)
    root.appendChild(this.trigger)

    this.presenter = this.createPresenter(this.shellConfig.panelLocation)
    this.presenter.mount(root)

    this.trigger.addEventListener('click', () => this.openSheet())
    this.startListeningForIframeMessages()
    this.syncViewportListener()
  }

  override applyPreviewShellConfig(config: ShellConfig): boolean {
    if (config.formFactor !== 'fab' || !this.root) {
      return false
    }

    this.shellConfig = config as FabConfig
    this.updateActivePreviewConfig(config)
    applyPanelCornerRadius(this.root, this.shellConfig.theme?.['--radius'])
    this.renderTriggerChrome()
    this.applyTriggerAlignment(this.shellConfig.panelLocation)
    this.swapPresenterIfNeeded(this.shellConfig.panelLocation)
    this.presenter.applyPlacement(this.shellConfig.panelLocation)
    this.presenter.setSpinnerColor(this.getFabAccentColor())

    if (this.isOpen) {
      const {parentUrl, pageTitle} = this.getLiveIframePageContext()
      this.currentIframeContextKey = null
      this.ensureIframe(parentUrl, pageTitle)
    }

    return true
  }

  private createPresenter(placement: PanelPlacement): PanelPresenter {
    const callbacks = {
      onDismissRequested: () => this.closeSheet(),
      onClosed: () => {
        this.isOpen = false
        this.trigger.classList.remove('hidden')
      },
    }

    if (presenterKindForPlacement(placement) === 'center-modal') {
      return new CenterModalPanel({
        fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
        spinnerColor: this.getFabAccentColor(),
        callbacks,
      })
    }

    return new SideSheetPanel({
      fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
      placement,
      mobileDrawer: this.usesSideSheetLayout,
      spinnerColor: this.getFabAccentColor(),
      callbacks,
    })
  }

  private swapPresenterIfNeeded(placement: PanelPlacement): boolean {
    if (!this.root || presenterKindForPlacement(placement) === this.presenter.kind) {
      return false
    }

    const wasOpen = this.isOpen
    this.presenter.destroy()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isIframeReady = false
    this.isOpen = false

    this.presenter = this.createPresenter(placement)
    this.presenter.mount(this.root)
    this.syncViewportListener()

    if (wasOpen) {
      this.openSheet()
    }

    return true
  }

  private syncViewportListener(): void {
    if (this.presenter.tracksViewport && !this.viewportResizeListener) {
      this.viewportResizeListener = () => this.presenter.syncViewport()
      window.addEventListener('resize', this.viewportResizeListener)
      window.visualViewport?.addEventListener('resize', this.viewportResizeListener, {passive: true})
      window.visualViewport?.addEventListener('scroll', this.viewportResizeListener, {passive: true})
      return
    }

    if (!this.presenter.tracksViewport && this.viewportResizeListener) {
      this.teardownViewportListener()
    }
  }

  private teardownViewportListener(): void {
    if (!this.viewportResizeListener) {
      return
    }
    window.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('scroll', this.viewportResizeListener)
    this.viewportResizeListener = null
  }

  private getFabAccentColor(): string {
    return this.shellConfig.fabColor || this.shellConfig.accentColor || '#000000'
  }

  private renderTriggerChrome(): void {
    if (!this.trigger) {
      return
    }

    const accentColor = this.getFabAccentColor()
    const foregroundColor = getContrastColor(accentColor)
    this.trigger.style.backgroundColor = accentColor
    this.trigger.style.color = foregroundColor
    this.trigger.replaceChildren(this.createTriggerIcon(foregroundColor))
  }

  private applyTriggerAlignment(placement: PanelPlacement): void {
    if (!this.trigger) {
      return
    }

    const isLeftAligned = placement === 'left'
    this.trigger.style.right = isLeftAligned ? 'auto' : '16px'
    this.trigger.style.left = isLeftAligned ? '16px' : 'auto'
  }

  override destroy(): void {
    this.teardownViewportListener()
    this.presenter?.destroy()
    this.currentIframe?.remove()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isOpen = false
    this.isIframeReady = false
    this.host?.remove()
    this.host = null
    this.root = null

    super.destroy()
  }

  protected override ownsIframeMessageSource(source: MessageEventSource): boolean {
    return source === this.currentIframe?.contentWindow
  }

  private createTriggerIcon(color: string): HTMLElement {
    if (!isFabIconPresetUrl(this.shellConfig.fabIconUrl)) {
      const icon = document.createElement('img')
      icon.src = this.shellConfig.fabIconUrl
      icon.alt = ''
      icon.setAttribute('aria-hidden', 'true')
      icon.style.width = '24px'
      icon.style.height = '24px'
      icon.style.display = 'block'
      icon.style.objectFit = 'contain'
      return icon
    }

    const icon = document.createElement('span')
    icon.style.width = '24px'
    icon.style.height = '24px'
    icon.style.display = 'block'
    icon.style.backgroundColor = color
    icon.style.webkitMaskImage = `url(${this.shellConfig.fabIconUrl})`
    icon.style.maskImage = `url(${this.shellConfig.fabIconUrl})`
    icon.style.webkitMaskRepeat = 'no-repeat'
    icon.style.maskRepeat = 'no-repeat'
    icon.style.webkitMaskPosition = 'center'
    icon.style.maskPosition = 'center'
    icon.style.webkitMaskSize = 'contain'
    icon.style.maskSize = 'contain'
    return icon
  }

  private openSheet(): void {
    if (this.isOpen) return
    this.isOpen = true
    this.trigger.classList.add('hidden')

    const {parentUrl, pageTitle} = this.getLiveIframePageContext()
    this.ensureIframe(parentUrl, pageTitle)

    if (this.isIframeReady) {
      this.presenter.showReady()
    }

    this.presenter.open()
  }

  private closeSheet(): void {
    if (!this.isOpen) return
    this.presenter.close()
  }

  protected onIframeReady(): void {
    this.isIframeReady = true
    this.presenter.showReady()
  }

  private ensureIframe(parentUrl: string, pageTitle: string): void {
    const contextKey = this.buildIframeContextKey(parentUrl, pageTitle)
    if (this.currentIframe && this.currentIframeContextKey === contextKey) {
      return
    }

    this.currentIframe?.remove()

    const iframeUrl = this.createIframeUrl('sheet')
    iframeUrl.searchParams.append('parentUrl', parentUrl)
    if (pageTitle) iframeUrl.searchParams.append('pageTitle', pageTitle)

    this.currentIframe = this.createIframe(iframeUrl)
    this.currentIframeContextKey = contextKey
    this.isIframeReady = false
    this.presenter.showLoading()
    this.presenter.iframeContainer.appendChild(this.currentIframe)
  }

  protected handleShellSpecificIframeMessage(msg: AnagramMessage): void {
    switch (msg.type) {
      case 'ANAGRAM_CLOSE_REQUESTED': {
        this.closeSheet()
        break
      }
      case 'ANAGRAM_RESIZE': {
        const nextHeight = Number(msg.height)
        if (!Number.isNaN(nextHeight) && nextHeight > 0) {
          this.presenter.setBodyHeight(nextHeight)
        }
        break
      }
      case 'ANAGRAM_START_OVER': {
        this.presenter.resetBodyHeight()
        break
      }
    }
  }
}
